import {
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
  useAnalytics as getAnalyticsClient
} from '@databases/analytics';
import { contextualMenuState } from '@databases/contextual-menu/contextual-menu-state';
import { getCachedViewSnapshotForSelections } from '@databases/database-state';
import {
  type HighlightsController,
  type HighlightsRenderer
} from '@databases/highlights';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type PanelManager } from '@databases/panel-types';
import {
  buildCellActions,
  buildColumnActions,
  buildRowActions
} from '@databases/selection-menu-actions';
import { createEffect } from '@databases/selections';
import {
  type SelectionsApiContract,
  type DatabaseAdapter
} from '@databases/selections-api-types';
import { type SelectionsActions } from '@databases/selections-api-types';
import { type SelectionsReader } from '@databases/selections-api-types';
import { getService } from '@databases/service-container';
import { type ViewChangeType } from '@databases/utils/database/view/view';

import { contextMenuState } from './state/context-menu-state.ts';
import { createSelectValueMenuContext } from './utils/create-select-value-menu-context.ts';

export interface ContextMenuEffectDeps {
  selectionsApi: SelectionsApiContract;
  databaseAdapter: DatabaseAdapter;
  contentId: string;
  formatMessage: MessageFormatter;
  panelManager: PanelManager;
  selectionsActions: SelectionsActions;
  currentViewId: string;
  highlightsController: HighlightsController;
  selectionsReader: SelectionsReader;
  getIsDatabaseRovoAvailable: () => boolean;
  getIsDBAiFirstCreationEnabled: () => boolean;
}

/**
 * Builds and displays the selection-aware value context menu after a
 * context-menu selection replacement. It resolves the focused cell to IDs,
 * applies permissions and business rules, owns the delete-preview layer, and
 * clears all portal/highlight state on unrelated edit deltas or disposal.
 */
export const createContextMenuEffect = createEffect(
  ({
    selectionsApi,
    databaseAdapter,
    contentId,
    formatMessage,
    panelManager,
    selectionsActions,
    currentViewId,
    highlightsController,
    selectionsReader,
    getIsDatabaseRovoAvailable,
    getIsDBAiFirstCreationEnabled
  }: ContextMenuEffectDeps) => {
    let deletePreviewLayer: HighlightsRenderer | null = null;

    function ensureDeletePreviewLayer(): HighlightsRenderer {
      if (!deletePreviewLayer) {
        deletePreviewLayer = highlightsController.createLayer();
      }

      return deletePreviewLayer;
    }

    function releaseDeletePreviewLayer() {
      if (deletePreviewLayer) {
        deletePreviewLayer.dispose();
        deletePreviewLayer = null;
      }
    }

    return {
      dispose() {
        contextMenuState.hide();
        releaseDeletePreviewLayer();
      },

      deltaTypes: ['selectionReplaced', 'cellEditRequested'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested' || !delta.contextMenuTrigger) {
          releaseDeletePreviewLayer();
          contextMenuState.hide();

          return;
        }

        const focusedPosition = delta.focusChange?.next;

        if (!focusedPosition) {
          releaseDeletePreviewLayer();
          contextMenuState.hide();

          return;
        }

        const { entryId, fieldId } = selectionsApi.resolvePositionToIds(
          focusedPosition,
          'cell'
        );

        if (!entryId || !fieldId) {
          releaseDeletePreviewLayer();
          contextMenuState.hide();

          return;
        }

        const permissions = getService('PermissionsManager').getPermissions();
        const layer = ensureDeletePreviewLayer();

        const context = createSelectValueMenuContext({
          canEditContent: permissions.canEditContent,
          canEditFields: permissions.canEditFields,
          closeMenu: () => {
            releaseDeletePreviewLayer();
            contextMenuState.hide();
          },
          contentId,
          currentViewId,
          databaseAdapter,
          deletePreviewLayer: layer,
          entryId,
          fieldId,
          formatMessage,
          isDatabaseRovoAvailable: getIsDatabaseRovoAvailable(),
          isDBAiFirstCreationEnabled: getIsDBAiFirstCreationEnabled(),
          isDeletionDisabled: selectionsReader.isDeleteColumnsDisabled(),
          panelManager,
          resolveEntryIdToRowIndex: (id: string) =>
            databaseAdapter.getVisibleEntryIds().indexOf(id),
          resolveFieldIdToColIndex: (id: string) =>
            databaseAdapter.getVisibleFieldIds().indexOf(id),
          selectionsActions,
          selectionsApi
        });

        if (!context) {
          releaseDeletePreviewLayer();
          contextMenuState.hide();

          return;
        }

        const view = getCachedViewSnapshotForSelections();
        const actionsBuilder = (setViewChange: (view: ViewChangeType) => void) => ({
          row: buildRowActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            spaceKey: databaseAdapter.getSpaceKey(),
            panelManager,
            isLocked: permissions.isLocked
          }),
          column: buildColumnActions({
            contentId,
            selectionsApi,
            view,
            databaseAdapter,
            setViewChange,
            formatMessage
          }),
          cell: buildCellActions({
            contentId,
            selectionsApi,
            panelManager
          })
        });

        if (contextualMenuState.isAnyMenuOpen()) {
          contextualMenuState.closeMenu();
        }

        contextMenuState.show(delta.contextMenuTrigger, context, actionsBuilder);

        const analyticsClient = getAnalyticsClient();

        void analyticsClient.sendValueContextMenuOpenedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          ...getSelectionsAnalyticsAttributes(contentId)
        });
      }
    };
  }
);
